import { CardType } from '../../../../models/card-model';
import type { TableCardConfig } from '../../../../models/table-config.model';
import type {
	ChartDataResponse,
	QueryCardDataResponse,
	TableDataResponse,
} from '../../../../models/card-model';
import type { TableData } from '../../../../models/chart-data.model';
import type { DataService } from '../../../../services/data.service';
import { extractRawCardDataFromGql } from '../../jepto-chart-editor/services/chart-preview-data.util';

export { extractRawCardDataFromGql };

function tableColumnsFromInner(
	inner: Record<string, unknown>
): Record<string, unknown> | undefined {
	const result = inner['result'] as
		| { columns?: Record<string, unknown> }
		| undefined;
	if (result?.columns && Object.keys(result.columns).length > 0) {
		return result.columns;
	}
	const flat = inner['columns'] as Record<string, unknown> | undefined;
	if (flat && Object.keys(flat).length > 0) {
		return flat;
	}
	return undefined;
}

/**
 * True when columnar payload has rows, or is an explicit empty result
 * (`totalRows` / `total` === 0). Intermediate WS blanks (column keys with
 * empty arrays, no zero-total) return false so they do not wipe the grid.
 */
function tableInnerHasRenderableRows(inner: Record<string, unknown>): boolean {
	const cols = tableColumnsFromInner(inner);
	if (!cols) {
		return false;
	}
	const keys = Object.keys(cols);
	if (keys.length === 0) {
		return false;
	}
	const first = cols[keys[0]] as unknown[] | undefined;
	const rowCount = Array.isArray(first) ? first.length : 0;
	if (rowCount > 0) {
		return true;
	}
	const pagination = inner['pagination'] as
		| { totalRows?: unknown }
		| undefined;
	if (typeof pagination?.totalRows === 'number') {
		return pagination.totalRows === 0;
	}
	if (typeof inner['total'] === 'number') {
		return inner['total'] === 0;
	}
	return false;
}

/** Moves flat `columns` / `columnDefs` under `data.result` when the backend omits `result`. */
export function normalizeTableDataInner(
	inner: Record<string, unknown>
): Record<string, unknown> {
	const result = inner['result'] as Record<string, unknown> | undefined;
	const resultCols = result?.['columns'] as
		| Record<string, unknown>
		| undefined;
	if (resultCols && Object.keys(resultCols).length > 0) {
		return inner;
	}

	const flatCols = inner['columns'] as Record<string, unknown> | undefined;
	if (!flatCols || Object.keys(flatCols).length === 0) {
		return inner;
	}

	const nextResult: Record<string, unknown> = { columns: flatCols };
	const columnDefs = inner['columnDefs'];
	if (Array.isArray(columnDefs) && columnDefs.length > 0) {
		nextResult['columnDefs'] = columnDefs;
	}
	const sorting = inner['sorting'];
	if (sorting && typeof sorting === 'object') {
		nextResult['sorting'] = sorting;
	}

	const {
		columns: _columns,
		columnDefs: _columnDefs,
		sorting: _sorting,
		...rest
	} = inner;

	return {
		...rest,
		result: nextResult,
	};
}

export function hasRenderableTablePayload(
	data: TableData | ChartDataResponse | null | undefined
): boolean {
	if (!data?.data) {
		return false;
	}
	const inner = data.data as Record<string, unknown>;
	return tableInnerHasRenderableRows(inner);
}

/** True when a settled payload has columns but zero rows. */
export function isSettledEmptyTablePayload(
	data: TableData | ChartDataResponse | null | undefined
): boolean {
	if (!hasRenderableTablePayload(data) || !data?.data) {
		return false;
	}
	const inner = data.data as Record<string, unknown>;
	const cols = tableColumnsFromInner(inner);
	if (!cols) {
		return false;
	}
	const keys = Object.keys(cols);
	if (keys.length === 0) {
		return false;
	}
	const first = cols[keys[0]] as unknown[] | undefined;
	const rowCount = Array.isArray(first) ? first.length : 0;
	return rowCount === 0;
}

/** True when `data.metrics` / `data.dimensions` include at least one schema field name. */
export function tableCardHasPreviewQueryFields(
	config: TableCardConfig | null | undefined
): boolean {
	const data = config?.data;
	if (!data) {
		return false;
	}
	const hasMetric = (data.metrics ?? []).some(
		m => typeof m?.name === 'string' && m.name.trim().length > 0
	);
	const hasDimension = (data.dimensions ?? []).some(
		d => typeof d?.name === 'string' && d.name.trim().length > 0
	);
	return hasMetric || hasDimension;
}

/** Stable fingerprint of table `getCardData` query shape (dimensions, metrics, filters, order). */
function tableCardDataQueryFingerprint(
	data: TableCardConfig['data'] | undefined
): string {
	if (!data) {
		return '';
	}
	const dimensions = (data.dimensions ?? [])
		.map(d => d.id ?? d.name ?? '')
		.filter(Boolean)
		.sort();
	const metrics = (data.metrics ?? [])
		.map(m => m.id ?? m.name ?? '')
		.filter(Boolean)
		.sort();
	return JSON.stringify({
		dimensions,
		metrics,
		filters: data.filters ?? null,
		order: data.order ?? null,
	});
}

export function tableCardDataQueryChanged(
	before: TableCardConfig['data'] | undefined,
	after: TableCardConfig['data'] | undefined
): boolean {
	return (
		tableCardDataQueryFingerprint(before) !==
		tableCardDataQueryFingerprint(after)
	);
}

/**
 * Fingerprint of the first dimension cell(s) in columnar table data.
 * Used to reject stale page-1 WS payloads while waiting for a remote page slice.
 */
export function tableDataFirstRowFingerprint(
	data: ChartDataResponse | TableData | null | undefined
): string {
	if (!data?.data) {
		return '';
	}
	const inner = data.data as Record<string, unknown>;
	const cols = tableColumnsFromInner(inner);
	if (!cols) {
		return '';
	}
	const keys = Object.keys(cols);
	if (keys.length === 0) {
		return '';
	}
	const firstCol = cols[keys[0]] as unknown[] | undefined;
	if (!firstCol?.length) {
		return '';
	}
	return firstCol
		.slice(0, 3)
		.map(cell => String(cell ?? ''))
		.join('\0');
}

export function tableDataPayloadFingerprint(
	data: ChartDataResponse | TableData | undefined
): string {
	if (!data) {
		return '';
	}
	const parts: string[] = [
		'requestId' in data && typeof data.requestId === 'string'
			? data.requestId
			: '',
	];
	const inner = data.data as Record<string, unknown> | undefined;
	if (inner) {
		const cols = tableColumnsFromInner(inner);
		if (cols) {
			const keys = Object.keys(cols);
			parts.push(`cols${keys.length}`);
			const first = keys[0];
			if (first) {
				const col = cols[first] as unknown[] | undefined;
				parts.push(`len${col?.length ?? 0}`);
			}
		}
		parts.push(`limit${String(inner['limit'] ?? '')}`);
		parts.push(`offset${String(inner['offset'] ?? '')}`);
	}
	return parts.join('|');
}

export function getTablePayloadExecutionId(
	payload: TableData | ChartDataResponse | null | undefined
): string {
	if (
		payload &&
		'executionId' in payload &&
		typeof payload.executionId === 'string'
	) {
		return payload.executionId;
	}
	return '';
}

export function resolveRenderableTablePreviewPayload(
	dataService: DataService,
	cardId: string
): TableData | ChartDataResponse | undefined {
	const fromTableMap = dataService.tableDataSignal(cardId)();
	const fromChartMap = tableDataFromChartDataResponse(
		dataService.cardDataSignal(cardId)(),
		cardId
	);
	return fromTableMap && hasRenderableTablePayload(fromTableMap)
		? fromTableMap
		: fromChartMap;
}

// fallow-ignore-next-line unused-export
export function tablePreviewDimensionLabel(config: TableCardConfig): string {
	const dim = config.data?.dimensions?.[0];
	return dim?.label?.trim() || dim?.name || 'Dimension';
}

/** AppSync may deliver `event` as a JSON string; coerce before routing. */
export function parseAppSyncCardEvent(
	raw: unknown
): Record<string, unknown> | null {
	if (raw == null) {
		return null;
	}
	if (typeof raw === 'string') {
		const trimmed = raw.trim();
		if (!trimmed.startsWith('{')) {
			return null;
		}
		try {
			const parsed = JSON.parse(trimmed) as unknown;
			return typeof parsed === 'object' &&
				parsed !== null &&
				!Array.isArray(parsed)
				? (parsed as Record<string, unknown>)
				: null;
		} catch {
			return null;
		}
	}
	if (typeof raw === 'object' && !Array.isArray(raw)) {
		return raw as Record<string, unknown>;
	}
	return null;
}

export function tableDataFromChartDataResponse(
	response: QueryCardDataResponse | null | undefined,
	cardId: string
): TableData | null {
	if (!response?.data || typeof response.data !== 'object') {
		return null;
	}
	const inner = normalizeTableDataInner(
		response.data as Record<string, unknown>
	);
	if (!tableInnerHasRenderableRows(inner)) {
		return null;
	}
	return {
		cardId,
		cardType: CardType.Table,
		data: inner as TableData['data'],
		...(response.requestId ? { requestId: response.requestId } : {}),
		...(response.executionId ? { executionId: response.executionId } : {}),
	} as TableData;
}

/** Build {@link TableData} from a parsed WebSocket card event (`messageType: data`, `cardType: table`). */
export function tableDataFromWebSocketEvent(event: {
	cardId: string;
	data?: unknown;
	requestId?: string;
	executionId?: string;
}): TableData | null {
	if (!event.cardId || event.data == null || typeof event.data !== 'object') {
		return null;
	}
	const inner = normalizeTableDataInner(
		event.data as Record<string, unknown>
	);
	if (!tableInnerHasRenderableRows(inner)) {
		return null;
	}
	return {
		cardId: event.cardId,
		cardType: CardType.Table,
		data: inner as TableData['data'],
		...(event.requestId ? { requestId: event.requestId } : {}),
		...(event.executionId ? { executionId: event.executionId } : {}),
	} as TableData;
}

function normalizeTableDataPayload(
	raw: unknown,
	_cardConfig: TableCardConfig
): Partial<TableData> | null {
	if (!raw || typeof raw !== 'object') {
		return null;
	}
	const obj = raw as Record<string, unknown>;
	const hasNestedData =
		'data' in obj &&
		typeof obj.data === 'object' &&
		obj.data !== null &&
		!Array.isArray(obj.data);
	const inner = hasNestedData ? (obj.data as Record<string, unknown>) : obj;
	const normalizedInner = normalizeTableDataInner(inner);
	if (!tableInnerHasRenderableRows(normalizedInner)) {
		return null;
	}
	if (hasNestedData) {
		return {
			...obj,
			data: normalizedInner,
		} as Partial<TableData>;
	}
	return {
		cardType: CardType.Table,
		data: normalizedInner as TableData['data'],
	} as TableData;
}

export function normalizeTableCardDataResponse(
	response: unknown,
	cardConfig: TableCardConfig
): TableData | null {
	const rawData = extractRawCardDataFromGql(response);
	if (typeof rawData === 'boolean') {
		return null;
	}
	let parsedData: unknown;
	if (typeof rawData === 'string') {
		try {
			parsedData = JSON.parse(rawData) as unknown;
		} catch {
			return null;
		}
	} else {
		parsedData = rawData;
	}
	if (!parsedData || typeof parsedData !== 'object') {
		return null;
	}
	const payload = normalizeTableDataPayload(parsedData, cardConfig);
	if (!payload) {
		return null;
	}
	return {
		...payload,
		cardId: cardConfig.cardId,
		cardType: CardType.Table,
	} as TableData;
}

/** Editor preview + report grid both read {@link DataService.cardDataMapSignal}. */
export function publishTablePreviewDataToDataService(
	dataService: DataService,
	cardId: string,
	normalized: TableData
): void {
	const withCardId = { ...normalized, cardId };
	dataService.updateTableData(cardId, withCardId);
	const layoutUpdate: Partial<TableDataResponse> = {
		cardId,
		cardType: CardType.Table,
		data: withCardId.data as TableDataResponse['data'],
	};
	if ('requestId' in withCardId && typeof withCardId.requestId === 'string') {
		layoutUpdate.requestId = withCardId.requestId;
	}
	if (
		'executionId' in withCardId &&
		typeof withCardId.executionId === 'string'
	) {
		layoutUpdate.executionId = withCardId.executionId;
	}
	dataService.updateCardData(layoutUpdate);
}

/** Normalize and store table payloads from AppSync (preview editor + layout grid). */
export function applyTableWebSocketDataToDataService(
	dataService: DataService,
	event: {
		cardId: string;
		data?: unknown;
		requestId?: string;
		executionId?: string;
	}
): boolean {
	const normalized = tableDataFromWebSocketEvent(event);
	if (!normalized) {
		return false;
	}
	publishTablePreviewDataToDataService(dataService, event.cardId, normalized);
	return true;
}

/**
 * When the client requested a page and the payload omits `offset`/`limit`,
 * stamp the requested values so Grid slice matching can succeed.
 * Does not overwrite an explicit server `offset`.
 */
export function stampTableDataRequestedPagination(
	data: TableData,
	pagination: { offset: number; limit: number } | undefined
): TableData {
	if (!pagination || !data.data || typeof data.data !== 'object') {
		return data;
	}
	const inner = data.data as Record<string, unknown>;
	if (typeof inner['offset'] === 'number') {
		return data;
	}
	return {
		...data,
		data: {
			...data.data,
			offset: Math.max(0, pagination.offset),
			limit: Math.max(1, pagination.limit),
		},
	};
}
